import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import Button from "components/common/Button";
import Typography from "components/common/Typography";
import { getUserDetails } from "components/features/SME/sme.slice";
import _, { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { includeAboveProperty } from "utils/CommonConstants";
import { getHltInncodes, getUserTypeValue } from "utils/CommonFunctions";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { fetchInncodeInfo, fetchMembersAsync } from "../PlanAssignment.slice";
import "./AssignTo.scss";

const AssignTo = (props: any) => {
  const dispatch = useAppDispatch();
  const [userData, setUserData] = useState<any>([]);
  const [inncodeData, setInncodeData] = useState<any>([]);
  const [groupedData, setGroupedData] = useState<any>([]);
  const [isViewAll, setIsViewAll] = useState<boolean>(false);
  const payloadData: any = useAppSelector((state) => state.PlanAssignment?.payloadData);
  const isAllMemberSelected = useAppSelector((state) => state.PlanAssignment.isAllMemberSelected);
  const unselecteMember = useAppSelector((state) => state.PlanAssignment.unselecteMember);

  const filters = useAppSelector((state: any) => state.PlanAssignment.memberSelection.filters);
  const { brand, region } = useAppSelector((state) => state.PlanAssignment.planSelection.filters);
  const totalMemberSelection = useAppSelector((state) => state.PlanAssignment.totalMemberSelection);

  const fetchHltId = () => {
    const users = payloadData?.users?.hltId;
    if (users) {
      const hltIds = users?.map((user: any) => {
        if (Array.isArray(user.hltIds)) {
          return user.hltIds[0];
        }
        return user.hltId;
      });
      const inncodes = users.map((inncode: any) => inncode.innCode);
      const groupedByInnCode = users.reduce((acc: any, item: any) => {
        (acc[item.innCode] = acc[item.innCode] || []).push(item);
        return acc;
      }, {});
      return { hltIds, groupedByInnCode, inncodes };
    }
    return [];
  };

  const fetchHltIdThird = () => {
    const users = isViewAll ? userData : payloadData?.users;
    const inncodes = users?.map((inncode: any) => inncode.innCodes[0]);
    const groupedByInnCode = users.reduce((acc: any, user: any) => {
      user.innCodes.forEach((innCode: any) => {
        if (!acc[innCode]) {
          acc[innCode] = [];
        }
        acc[innCode].push(user);
      });
      return acc;
    }, {});
    return { groupedByInnCode, inncodes };
  };

  const fetchHltIdFourth = () => {
    const users = payloadData?.users;
    const hltIds = users?.map((user: any) => user.hltId);
    const inncodes = users?.map((user: any) => user.inncode);
    const groupedByInnCode = _.groupBy(users, "inncode");
    return { groupedByInnCode, hltIds, inncodes };
  };

  const getMembersAPIPayload = () => {
    const meta = props.isPlanProgress ? props?.plans?.[0]?.meta : {};
    return {
      searchText: filters.searchText,
      innCodes: props.inncodes?.join(",") || filters?.property?.join(",") || getHltInncodes(true),
      page: 0,
      size: totalMemberSelection,
      roles: filters.role?.join(","),
      userType: getUserTypeValue(filters[includeAboveProperty]),
      ignoreNonFilteredInnCodes: true,
      respondWithHltIdAndInnCode: true,
      includecorpusers: false,
      ...(meta?.brandCodes?.length ? { brandCodes: meta.brandCodes } : brand?.length ? { brandCodes: brand } : {}),
      ...(meta?.regions?.length
        ? { regions: meta.regions?.join("::") }
        : region?.length
        ? { regions: region?.join("::") }
        : {}),
      ...(meta?.locationTypes?.length ? { locationTypes: meta.locationTypes } : {}),
      ...(meta?.innCodes?.length ? { tgInnCodes: meta.innCodes } : {}),
      ...(meta?.managementTypes?.length ? { mgmtTypes: meta.managementTypes } : {})
    };
  };

  useEffect(() => {
    (async () => {
      let userSelectionData: any;
      if (props.userSelectionFlow === "one" || props.userSelectionFlow === "two") {
        userSelectionData = fetchHltId();
      }
      if (props.userSelectionFlow === "third") {
        userSelectionData = fetchHltIdThird();
      }
      if (props.userSelectionFlow === "fourth") {
        userSelectionData = fetchHltIdFourth();
      }
      setGroupedData(userSelectionData.groupedByInnCode);
      if (!isViewAll) {
        if (userSelectionData.hltIds) {
          const userDetails = await dispatch(getUserDetails(userSelectionData.hltIds));
          setUserData(userDetails?.payload?.users || []);
        } else {
          const users = payloadData?.users;
          setUserData(users || []);
        }
      }
      const innCodePayload = {
        inncodes: userSelectionData.inncodes,
        propNameRequired: true
      };
      const inncodeInfo = await dispatch(fetchInncodeInfo(innCodePayload));
      setInncodeData(inncodeInfo?.payload?.innCodeDetails || []);
    })();
  }, [isViewAll]);

  const viewAll = async () => {
    const payload = getMembersAPIPayload();
    const res: any = await dispatch(fetchMembersAsync(payload));
    const result = res?.payload?.users.filter(
      (a1: any) => !unselecteMember.some((a2: any) => a2.hltId === a1.hltId && a2.innCodes[0] === a1.innCodes[0])
    );
    setUserData(result);
    setIsViewAll(true);
  };

  const totalUserSelectionLength = (userData?.length || 0) + (unselecteMember?.length || 0);
  return (
    <div className="assign-to-component">
      <Typography variant="body2" sx={{ marginBottom: "8px" }} color={tokens.Text.General.Secondary}>
        Assign to
      </Typography>
      <div className="assign-to-component-main-wrapper">
        <div className="assign-to-container">
          <div className="grid-container-heading">
            {["Property", "Inncode", "Member name", "Role"].map((heading) => {
              return (
                <div className="grid-item-heading" key={heading}>
                  <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
                    {heading}
                  </Typography>
                </div>
              );
            })}
          </div>
          <div className="grid-container-content">
            {!isEmpty(groupedData) &&
              Object.entries(groupedData)?.map((heading: any, index: number) => {
                const propertyName = inncodeData.find((inncode: any) => inncode.innCode === heading[0]);
                return (
                  <React.Fragment key={`${index}-brand-name`}>
                    <div className="grid-info">
                      <Typography variant="body2" showBold color="#312F2E">
                        {propertyName?.propName}
                      </Typography>
                    </div>
                    <div className="grid-info">
                      <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
                        {heading[0]}
                      </Typography>
                    </div>
                    <div className="grid-info-user">
                      {heading[1].map((item: any) => {
                        const userInfo = userData.find((user: any) => {
                          if (Array.isArray(item.hltIds)) {
                            return user.hltId === item.hltIds[0];
                          }
                          return user.hltId === item.hltId;
                        });
                        return (
                          <div className="user-wrapper">
                            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
                              {getUserNameWithConditionalCheck(
                                userInfo?.displayName,
                                userInfo?.firstName,
                                userInfo?.lastName
                              )}
                            </Typography>
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid-info-user">
                      {heading[1].map((item: any) => {
                        const userInfo = userData.find((user: any) => {
                          if (Array.isArray(item.hltIds)) {
                            return user.hltId === item.hltIds[0];
                          }
                          return user.hltId === item.hltId;
                        });
                        return (
                          <div className="user-wrapper">
                            <Typography variant="body2" color={tokens.Text.General.PrimaryBody}>
                              {userInfo?.roleCategory || " "}
                            </Typography>
                          </div>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              })}
          </div>
          {!isViewAll && isAllMemberSelected && totalUserSelectionLength < totalMemberSelection && (
            <div className="view-all-button">
              <Button
                variant="text"
                label={`View All (${totalMemberSelection - unselecteMember?.length})`}
                onClick={viewAll}
                endIcon={<ArrowDown />}
              ></Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignTo;
